import { Divider, Grid2 } from '@mui/material';
import { Form, Formik, FormikHelpers } from 'formik';
import { JobSiteDetails, JobSiteFormData } from '@/jobSites/application/types/JobSiteTypes';

import AddIcon from '@mui/icons-material/Add';
import Button from '@/common/presentation/components/inputs/Button';
import { FormikSubmitFormButton } from '@/common/presentation/components/forms/FormikSubmitFormButton/FormikSubmitFormButton';
import JobSiteDrawerFormFields from './JobSiteDrawerFormFields';
import { createJobSiteValidationSchema } from '@/jobSites/presentation/JobSiteValidationSchema';
import tid from '@/common/infrastructure/config/tid';
import { useMemo } from 'react';
import { useJobSiteForm } from '@/jobSites/presentation/hooks/useJobSiteForm';
import { useJobSiteTemporaryFormData } from '@/common/presentation/hooks/useJobSiteTemporaryFormData';

export default function JobSiteDrawerForm({
  initialValues,
  isNew,
  formikStatus,
  onClose,
  onInputActionClicked,
  loading,
  open
}: Readonly<{
  initialValues: JobSiteFormData;
  isNew: boolean;
  formikStatus: { loading: boolean };
  onClose: () => void;
  onInputActionClicked?: () => void;
  rawJobSite?: JobSiteDetails | null;
  loading: boolean;
  open: boolean;
}>) {
  const title = isNew ? 'Add JobSite' : 'Update JobSite';
  const { handleSubmit, updateLoading } = useJobSiteForm(isNew, onClose);
  const validationSchema = useMemo(() => createJobSiteValidationSchema(isNew), [isNew]);

  const handleSubmitWithCleanup = async (values: JobSiteFormData, helpers: FormikHelpers<JobSiteFormData>) => {
    await handleSubmit(values, helpers);
    if (isNew) {
      clearJobSiteFormData();
    }
  };

  const { clearJobSiteFormData } = useJobSiteTemporaryFormData();

  const handleClose = () => {
    if (isNew) {
      clearJobSiteFormData();
    }
    onClose();
  };

  return (
    <Formik
      key={isNew ? 'new' : initialValues.id}
      enableReinitialize
      validateOnChange
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmitWithCleanup}
      initialStatus={formikStatus}
    >
      {({ isSubmitting }) => (
        <Form>
          {/* JobSite Form Fields */}
          <JobSiteDrawerFormFields onInputActionClicked={onInputActionClicked ?? handleClose} />

          <Divider />

          {/* Action Buttons */}
          <Grid2 container spacing={2} sx={{ paddingY: 2, paddingX: 3 }}>
            {/* Cancel and Save Buttons */}
            <Grid2 size={12} display="flex" justifyContent="flex-end" alignItems="center">
              <Button
                variant="text"
                onClick={handleClose}
                sx={{ textTransform: 'none', fontWeight: 500, marginRight: 2 }}
                disabled={isSubmitting || updateLoading}
                {...tid('job-sites:management:cancel-button')}
              >
                Cancel
              </Button>

              <FormikSubmitFormButton
                isHotkeyEnabled={open}
                variant="contained"
                sx={{ paddingX: 3 }}
                startIcon={<AddIcon />}
                type="submit"
                disabled={isSubmitting || updateLoading || loading}
                testId="job-sites:management:save-button"
              >
                {isSubmitting || updateLoading ? 'Saving...' : title}
              </FormikSubmitFormButton>
            </Grid2>
          </Grid2>
        </Form>
      )}
    </Formik>
  );
}
